import { useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useResetPasswordConfirmMutation } from './authApiSlice'
import PulseLoader from 'react-spinners/PulseLoader'
import useTitle from '../../hooks/useTitle'
import { useTranslation } from 'react-i18next'
import AuthShell from '../../components/AuthShell'
import PasswordField from '../../components/PasswordField'

const ResetPassword = () => {
    const { t } = useTranslation()
    useTitle(t('titles.resetPassword'))

    const emailRef = useRef()
    const errRef = useRef()

    const [email, setEmail] = useState('')
    const [token, setToken] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [errMsg, setErrMsg] = useState('')
    const [successMsg, setSuccessMsg] = useState('')

    const navigate = useNavigate()

    const [resetPasswordConfirm, { isLoading }] = useResetPasswordConfirmMutation()

    useEffect(() => {
        emailRef.current?.focus()
    }, [])

    useEffect(() => {
        setErrMsg('')
    }, [email, token, newPassword, confirmPassword])

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (newPassword !== confirmPassword) {
            setErrMsg(t('auth.errors.passwordsDoNotMatch'))
            errRef.current.focus()
            return
        }

        try {
            await resetPasswordConfirm({ token, email, newPassword }).unwrap()
            setSuccessMsg(t('auth.reset.success'))
            setTimeout(() => navigate('/login'), 3000)
        } catch (err) {
            if (!err.status) {
                setErrMsg(t('auth.errors.noServerResponse'))
            } else if (err.status === 400) {
                setErrMsg(err.data?.message || t('auth.reset.errors.invalidCode'))
            } else if (err.status === 404) {
                setErrMsg(t('auth.reset.errors.notFound'))
            } else {
                setErrMsg(err.data?.message || t('auth.reset.errors.failed'))
            }
            errRef.current.focus()
        }
    }

    const errClass = errMsg ? 'errmsg' : 'offscreen'

    if (isLoading) return <PulseLoader color={'#e8c56d'} />

    return (
        <AuthShell
            eyebrow={t('auth.eyebrow')}
            title={t('auth.reset.heading')}
            subtitle={successMsg ? undefined : t('auth.reset.subtitle')}
            links={[
                { to: '/forgot-password', label: t('auth.forgot.resend') },
                { to: '/login', label: t('auth.backToSignIn') },
            ]}
        >
            <p ref={errRef} className={errClass} aria-live="assertive" tabIndex={-1}>{errMsg}</p>

            {successMsg ? (
                <div className="auth__body" aria-live="polite">
                    <p className="successmsg">{successMsg}</p>
                </div>
            ) : (
                <form className="form" onSubmit={handleSubmit}>
                    <div className="auth__field">
                        <label className="form__label" htmlFor="email">{t('auth.email')}</label>
                        <input
                            className="form__input"
                            type="email"
                            id="email"
                            ref={emailRef}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                            required
                        />
                    </div>

                    <div className="auth__field">
                        <label className="form__label" htmlFor="token">{t('auth.reset.code')}</label>
                        <input
                            className="form__input auth__code"
                            type="text"
                            id="token"
                            value={token}
                            onChange={(e) => setToken(e.target.value)}
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={6}
                            placeholder={t('auth.reset.codePlaceholder')}
                            required
                        />
                    </div>

                    <PasswordField
                        id="newPassword"
                        label={t('auth.reset.newPassword')}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        autoComplete="new-password"
                    />

                    <PasswordField
                        id="confirmPassword"
                        label={t('auth.reset.confirmNewPassword')}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        autoComplete="new-password"
                    />

                    <button className="form__submit-button">{t('auth.reset.submit')}</button>
                </form>
            )}
        </AuthShell>
    )
}

export default ResetPassword
